import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Logo } from '@/components/brand/logo'
import { LanguageSwitch } from '@/components/site/language-switch'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'
import { serviceSummaries } from '@/content/services-index'

export const Footer: FC = (): ReactNode => {
    const locale = useLocale()
    const copy = common[locale]

    const columns = [
        {
            title: copy.footer.services,
            links: serviceSummaries.map(item => ({ href: `/services/${item.slug}`, label: item.name[locale] }))
        },
        {
            title: copy.footer.company,
            links: [
                { href: '/about', label: copy.nav.about },
                { href: '/industries', label: copy.nav.industries },
                { href: '/ai-solutions', label: copy.nav.aiSolutions },
                { href: '/pricing', label: copy.nav.pricing },
                { href: '/contact', label: copy.nav.contact }
            ]
        },
        {
            title: copy.footer.resources,
            links: [
                { href: '/insights', label: copy.nav.insights },
                { href: '/blog', label: copy.nav.blog },
                { href: '/case-studies', label: copy.nav.caseStudies }
            ]
        },
        {
            title: copy.footer.portal,
            links: [
                { href: '/client-portal', label: copy.footer.portalOverview },
                { href: '/portal/login', label: copy.footer.login },
                { href: '/portal/register', label: copy.footer.register }
            ]
        }
    ]

    return (
        <footer className='relative overflow-hidden bg-ink text-white'>
            <div className='bg-grid-dark mask-fade-b pointer-events-none absolute inset-0 opacity-40' aria-hidden='true' />

            <div className='container-page relative pb-10 pt-20'>
                <div className='grid gap-12 lg:grid-cols-[1.2fr_2fr]'>
                    <div className='max-w-sm'>
                        <Logo tone='light' />

                        <p className='mt-5 text-[0.95rem] leading-relaxed text-white/65'>
                            {copy.footer.statement}
                        </p>

                        <p className='mt-6 font-display text-2xl italic text-white/90'>
                            {copy.tagline}
                        </p>

                        <dl className='mt-8 space-y-4 text-sm'>
                            <div>
                                <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-white/45'>
                                    {copy.footer.office}
                                </dt>

                                <dd className='mt-1 text-white/80'>
                                    {copy.footer.officeValue}
                                </dd>
                            </div>

                            <div>
                                <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-white/45'>
                                    {copy.footer.markets}
                                </dt>

                                <dd className='mt-1 text-white/80'>
                                    {copy.footer.marketsList.join(' · ')}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className='grid grid-cols-2 gap-8 sm:grid-cols-4'>
                        {columns.map(column => (
                            <div key={column.title}>
                                <h2 className='text-xs font-semibold uppercase tracking-[0.16em] text-white/45'>
                                    {column.title}
                                </h2>

                                <ul className='mt-4 space-y-2.5'>
                                    {column.links.map(link => (
                                        <li key={link.href}>
                                            <Link href={link.href} className='text-sm text-white/75 transition-colors hover:text-white'>
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        <span suppressHydrationWarning>
                            {`© ${new Date().getFullYear()} Ura Advisory. ${copy.footer.rights}`}
                        </span>
                        <span className='ml-2 hidden md:inline'>
                            {copy.footer.brandNote}
                        </span>
                    </p>

                    <div className='flex flex-wrap items-center gap-4'>
                        <Link href='/privacy' className='hover:text-white'>
                            {copy.footer.privacy}
                        </Link>

                        <Link href='/terms' className='hover:text-white'>
                            {copy.footer.terms}
                        </Link>

                        <LanguageSwitch tone='dark' />
                    </div>
                </div>
            </div>
        </footer>
    )
}
